export class ApiError extends Error {
  constructor({
    message,
    code = "UNEXPECTED_ERROR",
    status,
    details = null,
    type = "system",
  }) {
    super(message);
    this.name = "ApiError";
    this.code = code;
    this.status = status;
    this.details = details;
    this.type = type;
  }
}

const isDev = process.env.NODE_ENV !== "production";

const getErrorType = (status, code) => {
  const upperCode = String(code || "").toUpperCase();
  if (status === 400 || status === 422 || upperCode.includes("VALIDATION")) {
    return "validation";
  }

  if (status === 401 || status === 403 || status === 404 || status === 409) {
    return "business";
  }

  return "system";
};

const parseResponseBody = async (response) => {
  const contentType = response.headers.get("content-type") || "";

  if (contentType.includes("application/json")) {
    return response.json().catch(() => ({}));
  }

  if (contentType.includes("text/")) {
    const text = await response.text().catch(() => "");
    return { message: text };
  }

  return {};
};

const getFallbackByStatus = (status, language = "pt") => {
  const ptMessages = {
    400: "Dados inválidos. Verifique os campos e tente novamente.",
    401: "Sua sessão expirou. Faça login novamente.",
    403: "Você não tem permissão para realizar esta ação.",
    404: "Não foi possível encontrar o recurso solicitado.",
    409: "Conflito de dados. Revise as informações e tente novamente.",
    500: "Erro interno no servidor. Tente novamente em instantes.",
  };

  const enMessages = {
    400: "Invalid data. Check the fields and try again.",
    401: "Your session has expired. Please sign in again.",
    403: "You do not have permission to perform this action.",
    404: "The requested resource could not be found.",
    409: "Data conflict. Review your information and try again.",
    500: "Internal server error. Please try again shortly.",
  };

  const table = language === "pt" ? ptMessages : enMessages;
  return table[status] || (language === "pt" ? "Ocorreu um erro inesperado." : "An unexpected error occurred.");
};

export const getFriendlyErrorMessage = (error, language = "pt") => {
  if (!error) {
    return getFallbackByStatus(undefined, language);
  }

  if (error instanceof ApiError) {
    if (error.type === "validation") {
      return error.message || getFallbackByStatus(400, language);
    }

    if (error.type === "business") {
      return error.message || getFallbackByStatus(error.status, language);
    }

    return getFallbackByStatus(error.status, language);
  }

  return error.message || getFallbackByStatus(undefined, language);
};

export const logSystemError = (error, context = "api") => {
  if (!error) {
    return;
  }

  if (isDev) {
    // Placeholder for future external telemetry integration
    console.error(`[${context}]`, {
      name: error.name,
      message: error.message,
      code: error.code,
      status: error.status,
      details: error.details,
    });
  }
};

export const extractFieldErrors = (error) => {
  if (!(error instanceof ApiError) || !error.details) {
    return null;
  }

  if (error.details.fieldErrors && typeof error.details.fieldErrors === "object") {
    return error.details.fieldErrors;
  }

  if (Array.isArray(error.details.errors)) {
    return error.details.errors.reduce((acc, item) => {
      if (item?.field && item?.message) {
        acc[item.field] = item.message;
      }
      return acc;
    }, {});
  }

  return null;
};

export const apiRequest = async (
  url,
  {
    method = "GET",
    headers = {},
    body,
    token,
    responseType = "json",
    fallbackMessage,
    errorCode,
  } = {}
) => {
  const finalHeaders = { ...headers };

  if (token) {
    finalHeaders.Authorization = `Bearer ${token}`;
  }

  const isFormData = body instanceof FormData;
  if (!isFormData && body !== undefined && !finalHeaders["Content-Type"]) {
    finalHeaders["Content-Type"] = "application/json";
  }

  const requestBody =
    body === undefined
      ? undefined
      : isFormData || typeof body === "string"
      ? body
      : JSON.stringify(body);

  const response = await fetch(url, {
    method,
    headers: finalHeaders,
    body: requestBody,
  });

  if (!response.ok) {
    const parsed = await parseResponseBody(response);
    const message =
      parsed?.message || parsed?.error || fallbackMessage || getFallbackByStatus(response.status);
    const code = parsed?.code || errorCode || `HTTP_${response.status}`;
    const details = parsed?.details || parsed || null;
    const type = getErrorType(response.status, code);
    throw new ApiError({
      message,
      code,
      status: response.status,
      details,
      type,
    });
  }

  if (responseType === "blob") {
    return response.blob();
  }

  if (responseType === "text") {
    return response.text();
  }

  if (responseType === "raw") {
    return response;
  }

  if (response.status === 204) {
    return {};
  }

  return response.json().catch(() => ({}));
};